import {
  useState,
  useRef,
  useEffect,
  useId,
  ReactNode,
  useCallback,
  Fragment,
} from "react";

export type DropdownOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type ListboxProps = {
  role: "listbox";
  id: string;
  tabIndex: -1;
  "aria-label"?: string;
  onKeyDown: (e: React.KeyboardEvent) => void;
};

export type Props = {
  /** Dropdown options */
  options: DropdownOption[];
  /** Currently selected value */
  value?: string;
  /** Change handler */
  onChange?: (value: string) => void;
  /** Whether the dropdown is disabled */
  disabled?: boolean;
  /** Placeholder text when no value is selected */
  placeholder?: string;
  /** Additional CSS classes for the container */
  className?: string;
  /** Custom trigger renderer */
  renderTrigger?: (props: {
    isOpen: boolean;
    selectedOption?: DropdownOption;
    placeholder?: string;
    disabled?: boolean;
    onClick: () => void;
    onKeyDown: (e: React.KeyboardEvent) => void;
    /** ID of the listbox element, for aria-controls */
    listboxId: string;
  }) => ReactNode;
  /** Custom menu renderer */
  renderMenu?: (props: {
    children: ReactNode;
    listboxProps: ListboxProps;
  }) => ReactNode;
  /** Custom menu item renderer */
  renderItem?: (props: {
    option: DropdownOption;
    isSelected: boolean;
    isHighlighted: boolean;
    onClick: () => void;
    /** Unique ID for this option, use as the element's id attribute */
    id: string;
    /** Use as tabIndex on the option element (0 = highlighted, -1 = not) */
    tabIndex: 0 | -1;
  }) => ReactNode;
  /** Accessibility label */
  "aria-label"?: string;
  /** Test ID for testing */
  "data-testid"?: string;
};

/**
 * Base dropdown component with no styling.
 * Handles keyboard navigation, click outside, and accessibility.
 * Use render props to customize appearance.
 *
 * Uses roving tabindex for focus management: when the dropdown opens,
 * focus moves to the highlighted option element. Arrow keys move focus
 * between options. When the dropdown closes, focus returns to the trigger.
 *
 * @example
 * // In shared/ui/Dropdown.tsx
 * import { DropdownBase, type Props as DropdownBaseProps } from "@core/ui";
 *
 * export function Dropdown({ className, ...props }: DropdownBaseProps) {
 *   return (
 *     <DropdownBase
 *       {...props}
 *       renderTrigger={({ selectedOption, placeholder, onClick, listboxId }) => (
 *         <button onClick={onClick} className="dropdown-trigger" aria-controls={listboxId}>
 *           {selectedOption?.label || placeholder}
 *         </button>
 *       )}
 *       renderMenu={({ children, listboxProps }) => (
 *         <div {...listboxProps} className="dropdown-menu">
 *           {children}
 *         </div>
 *       )}
 *       renderItem={({ option, isSelected, onClick, id, tabIndex }) => (
 *         <div id={id} role="option" aria-selected={isSelected} tabIndex={tabIndex} onClick={onClick}>
 *           {option.label}
 *         </div>
 *       )}
 *     />
 *   );
 * }
 */
export function DropdownBase({
  options,
  value,
  onChange,
  disabled = false,
  placeholder = "Select...",
  className,
  renderTrigger,
  renderMenu,
  renderItem,
  "aria-label": ariaLabel,
  "data-testid": testId = "dropdown",
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;

  const selectedOption = options.find((opt) => opt.value === value);

  const getOptionId = (index: number) => `${baseId}-option-${index}`;

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setHighlightedIndex(-1);
    openerRef.current?.focus();
  }, []);

  // Roving tabindex: focus the highlighted option when it changes
  useEffect(() => {
    if (!isOpen || highlightedIndex < 0) return;
    const optionId = getOptionId(highlightedIndex);
    const el = document.getElementById(optionId);
    if (el) {
      el.focus();
      if (typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "nearest" });
      }
    }
  }, [isOpen, highlightedIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
        openerRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleToggle = useCallback(() => {
    if (disabled) return;
    if (!isOpen) {
      openerRef.current = document.activeElement as HTMLElement;
      setIsOpen(true);
      const selectedIndex = options.findIndex((opt) => opt.value === value);
      setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    } else {
      handleClose();
    }
  }, [disabled, isOpen, options, value, handleClose]);

  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange?.(optionValue);
      setIsOpen(false);
      setHighlightedIndex(-1);
      openerRef.current?.focus();
    },
    [onChange]
  );

  // Keyboard handler for the trigger button (when menu is closed)
  const handleTriggerKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;

      switch (e.key) {
        case "Enter":
        case " ":
          e.preventDefault();
          handleToggle();
          break;
        case "ArrowDown":
          e.preventDefault();
          handleToggle();
          break;
      }
    },
    [disabled, handleToggle]
  );

  // Keyboard handler for the listbox/options (when menu is open)
  const handleMenuKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case "Enter":
        case " ":
          e.preventDefault();
          if (highlightedIndex >= 0) {
            const option = options[highlightedIndex];
            if (option && !option.disabled) {
              handleSelect(option.value);
            }
          }
          break;
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) => {
            const nextIndex = prev + 1;
            for (let i = nextIndex; i < options.length; i++) {
              if (!options[i].disabled) return i;
            }
            return prev;
          });
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) => {
            const nextIndex = prev - 1;
            for (let i = nextIndex; i >= 0; i--) {
              if (!options[i].disabled) return i;
            }
            return prev;
          });
          break;
        case "Home":
          e.preventDefault();
          for (let i = 0; i < options.length; i++) {
            if (!options[i].disabled) {
              setHighlightedIndex(i);
              break;
            }
          }
          break;
        case "End":
          e.preventDefault();
          for (let i = options.length - 1; i >= 0; i--) {
            if (!options[i].disabled) {
              setHighlightedIndex(i);
              break;
            }
          }
          break;
        case "Escape":
          e.preventDefault();
          handleClose();
          break;
        case "Tab":
          handleClose();
          break;
      }
    },
    [highlightedIndex, options, handleSelect, handleClose]
  );

  // Listbox props to be spread on the menu element
  const listboxProps: ListboxProps = {
    role: "listbox",
    id: listboxId,
    tabIndex: -1,
    "aria-label": ariaLabel,
    onKeyDown: handleMenuKeyDown,
  };

  // Default trigger rendering
  const defaultTrigger = (
    <button
      type="button"
      onClick={handleToggle}
      onKeyDown={handleTriggerKeyDown}
      disabled={disabled}
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      aria-controls={listboxId}
      aria-label={ariaLabel}
      data-dropdown-trigger
    >
      {selectedOption?.label || placeholder}
    </button>
  );

  // Default menu item rendering
  const defaultItem = (
    option: DropdownOption,
    isSelected: boolean,
    isHighlighted: boolean,
    optionId: string
  ) => (
    <div
      key={option.value}
      id={optionId}
      role="option"
      aria-selected={isSelected}
      aria-disabled={option.disabled || undefined}
      tabIndex={isHighlighted ? 0 : -1}
      onClick={() => !option.disabled && handleSelect(option.value)}
      data-dropdown-item
      data-highlighted={isHighlighted}
      data-selected={isSelected}
    >
      {option.label}
    </div>
  );

  // Build menu items
  const menuItems = options.map((option, index) => {
    const isSelected = option.value === value;
    const isHighlighted = index === highlightedIndex;
    const optionId = getOptionId(index);

    if (renderItem) {
      return (
        <Fragment key={option.value}>
          {renderItem({
            option,
            isSelected,
            isHighlighted,
            onClick: () => !option.disabled && handleSelect(option.value),
            id: optionId,
            tabIndex: isHighlighted ? 0 : -1,
          })}
        </Fragment>
      );
    }

    return defaultItem(option, isSelected, isHighlighted, optionId);
  });

  // Default menu rendering
  const defaultMenu = (
    <div {...listboxProps} data-dropdown-menu>
      {menuItems}
    </div>
  );

  return (
    <div ref={containerRef} className={className} data-testid={testId}>
      {renderTrigger
        ? renderTrigger({
            isOpen,
            selectedOption,
            placeholder,
            disabled,
            onClick: handleToggle,
            onKeyDown: handleTriggerKeyDown,
            listboxId,
          })
        : defaultTrigger}
      {isOpen &&
        (renderMenu
          ? renderMenu({ children: menuItems, listboxProps })
          : defaultMenu)}
    </div>
  );
}

DropdownBase.displayName = "DropdownBase";
